import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Spade, Heart, Diamond, Club, ArrowRight, Trophy, TrendingUp, TrendingDown, Coins } from "lucide-react";

// Poker hand rankings data
const POKER_HANDS = [
  {
    rank: 1,
    name: "ロイヤルストレートフラッシュ",
    description: "同じスートのA-K-Q-J-10",
    example: ["A♠", "K♠", "Q♠", "J♠", "10♠"],
    color: "text-yellow-400",
    bgColor: "bg-yellow-500/20",
  },
  {
    rank: 2,
    name: "ストレートフラッシュ",
    description: "同じスートの連続した5枚",
    example: ["9♥", "8♥", "7♥", "6♥", "5♥"],
    color: "text-purple-400",
    bgColor: "bg-purple-500/20",
  },
  {
    rank: 3,
    name: "フォーカード",
    description: "同じ数字が4枚",
    example: ["K♠", "K♥", "K♦", "K♣", "2♠"],
    color: "text-pink-400",
    bgColor: "bg-pink-500/20",
  },
  {
    rank: 4,
    name: "フルハウス",
    description: "スリーカード + ワンペア",
    example: ["Q♠", "Q♥", "Q♦", "7♣", "7♠"],
    color: "text-blue-400",
    bgColor: "bg-blue-500/20",
  },
  {
    rank: 5,
    name: "フラッシュ",
    description: "同じスートの5枚",
    example: ["A♦", "J♦", "8♦", "6♦", "3♦"],
    color: "text-cyan-400",
    bgColor: "bg-cyan-500/20",
  },
  {
    rank: 6,
    name: "ストレート",
    description: "連続した数字の5枚",
    example: ["10♠", "9♥", "8♦", "7♣", "6♠"],
    color: "text-green-400",
    bgColor: "bg-green-500/20",
  },
  {
    rank: 7,
    name: "スリーカード",
    description: "同じ数字が3枚",
    example: ["8♠", "8♥", "8♦", "K♣", "4♠"],
    color: "text-orange-400",
    bgColor: "bg-orange-500/20",
  },
  {
    rank: 8,
    name: "ツーペア",
    description: "ペアが2組",
    example: ["J♠", "J♥", "5♦", "5♣", "A♠"],
    color: "text-amber-400",
    bgColor: "bg-amber-500/20",
  },
  {
    rank: 9,
    name: "ワンペア",
    description: "同じ数字が2枚",
    example: ["10♠", "10♥", "A♦", "7♣", "3♠"],
    color: "text-gray-400",
    bgColor: "bg-gray-500/20",
  },
  {
    rank: 10,
    name: "ハイカード",
    description: "役なし（最も高いカード）",
    example: ["A♠", "J♥", "8♦", "6♣", "2♠"],
    color: "text-gray-500",
    bgColor: "bg-gray-600/20",
  },
];

function CardDisplay({ card }) {
  const isRed = card.includes("♥") || card.includes("♦");
  return (
    <span className={`font-mono text-xs md:text-sm ${isRed ? "text-red-500" : "text-white"}`}>
      {card}
    </span>
  );
}

export default function RulesModal({ open, onClose }) {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col bg-card border-brass/30">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="text-xl font-display text-brass flex items-center gap-2">
            <Trophy className="w-5 h-5" />
            ルール / ポーカー手役確認
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto pr-2 space-y-6 pb-4">
          {/* Scoring Rules Section */}
          <section>
            <h3 className="text-lg font-bold text-amber-400 mb-3 flex items-center gap-2">
              <Coins className="w-5 h-5" />
              得点ルール
            </h3>
            
            <div className="space-y-3">
              {/* Basic Flow */}
              <div className="bg-muted/50 rounded-lg p-3 border border-brass/20">
                <h4 className="font-bold text-sm mb-2 text-white">ゲームの流れ</h4>
                <div className="flex items-center gap-2 text-xs md:text-sm flex-wrap">
                  <span className="bg-blue-500/30 px-2 py-1 rounded">配る</span>
                  <ArrowRight className="w-3 h-3 text-muted-foreground" />
                  <span className="bg-purple-500/30 px-2 py-1 rounded">公開</span>
                  <ArrowRight className="w-3 h-3 text-muted-foreground" />
                  <span className="bg-green-500/30 px-2 py-1 rounded">交渉</span>
                  <ArrowRight className="w-3 h-3 text-muted-foreground" />
                  <span className="bg-amber-500/30 px-2 py-1 rounded">結果</span>
                </div>
              </div>

              {/* Chip Movement */}
              <div className="bg-muted/50 rounded-lg p-3 border border-brass/20">
                <h4 className="font-bold text-sm mb-2 text-white">チップの移動</h4>
                <div className="space-y-2 text-xs md:text-sm">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-green-400" />
                    <span className="text-green-400 font-bold">1位:</span>
                    <span>+3チップ獲得</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-blue-400" />
                    <span className="text-blue-400 font-bold">2位:</span>
                    <span>+1チップ獲得</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <TrendingDown className="w-4 h-4 text-orange-400" />
                    <span className="text-orange-400 font-bold">3位:</span>
                    <span>-1チップ</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <TrendingDown className="w-4 h-4 text-red-400" />
                    <span className="text-red-400 font-bold">4位:</span>
                    <span>-3チップ</span>
                  </div>
                </div>
              </div>

              {/* Card Rules */}
              <div className="bg-muted/50 rounded-lg p-3 border border-brass/20">
                <h4 className="font-bold text-sm mb-2 text-white">カードのルール</h4>
                <ul className="space-y-1 text-xs md:text-sm text-muted-foreground">
                  <li>• 各プレイヤーに<span className="text-white font-bold">7枚</span>のカードが配られます</li>
                  <li>• そのうち<span className="text-white font-bold">4枚</span>を公開カードとして選択</li>
                  <li>• 残り<span className="text-white font-bold">3枚</span>は非公開（秘密）カード</li>
                  <li>• 交渉フェーズでは<span className="text-amber-400 font-bold">公開カードのみ</span>取引可能</li>
                  <li>• 最終的に<span className="text-white font-bold">5枚</span>でポーカー役を作成</li>
                </ul>
              </div>

              {/* Trading Tips */}
              <div className="bg-muted/50 rounded-lg p-3 border border-brass/20">
                <h4 className="font-bold text-sm mb-2 text-white">交渉のコツ</h4>
                <ul className="space-y-1 text-xs md:text-sm text-muted-foreground">
                  <li>• カード同士、またはカード＋チップで交換できます</li>
                  <li>• 相手の公開カードをよく見て交渉しましょう</li>
                  <li>• 自分のカードに「譲渡可」「譲渡不可」マークを付けられます</li>
                  <li>• 相手のカードに興味チップを置けます</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Poker Hands Section */}
          <section>
            <h3 className="text-lg font-bold text-amber-400 mb-3 flex items-center gap-2">
              <div className="flex gap-0.5">
                <Spade className="w-4 h-4" />
                <Heart className="w-4 h-4 text-red-500" />
                <Diamond className="w-4 h-4 text-red-500" />
                <Club className="w-4 h-4" />
              </div>
              ポーカー手役（強い順）
            </h3>

            <div className="space-y-2">
              {POKER_HANDS.map((hand) => (
                <div
                  key={hand.rank}
                  className={`${hand.bgColor} rounded-lg p-2 md:p-3 border border-white/10`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className={`font-bold text-lg md:text-xl ${hand.color}`}>
                        #{hand.rank}
                      </span>
                      <div className="min-w-0">
                        <div className={`font-bold text-sm md:text-base ${hand.color} truncate`}>
                          {hand.name}
                        </div>
                        <div className="text-xs text-muted-foreground hidden md:block">
                          {hand.description}
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-1 flex-shrink-0 bg-black/30 px-2 py-1 rounded">
                      {hand.example.map((card, i) => (
                        <CardDisplay key={i} card={card} />
                      ))}
                    </div>
                  </div>
                  <div className="text-xs text-muted-foreground md:hidden mt-1">
                    {hand.description}
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>
      </DialogContent>
    </Dialog>
  );
}
